import { doc, getDoc, writeBatch } from 'firebase/firestore'
import { db } from '../store/firebase'
import {
  BillingItem,
  CreditNote as LegacyCreditNote,
  Invoice as LegacyInvoice,
  Patient,
  Payment as LegacyPayment,
  Quotation as LegacyQuotation,
  SalesReceipt,
  User,
  WalkInClient,
} from '../types'
import {
  BillLine,
  BillPayment,
  BillingAccount,
  BillingConfig,
  CatalogueItem,
  CreditNote,
  MigrationReport,
  NumberSeries,
  SalesDocument,
} from '../types/billing'
import { BCOL, CONFIG_ID, clean, counterRef, numberRef } from './billingFirestore'
import { addK, fromN, subK, toN } from './billingMoney'

export type LegacyBilling = {
  invoices: LegacyInvoice[]
  quotations: LegacyQuotation[]
  payments: LegacyPayment[]
  creditNotes: LegacyCreditNote[]
  salesReceipts: SalesReceipt[]
  walkInClients: WalkInClient[]
  billingItems: BillingItem[]
  patients: Patient[]
}

const methodMap: Record<LegacyPayment['method'], [string, string]> = {
  Cash: ['cash', 'Cash'],
  'Mobile Money': ['mobile-money', 'Mobile Money'],
  'Debit Card': ['card', 'Card'],
  'Credit Card': ['card', 'Card'],
  'Bank Transfer': ['bank-transfer', 'Bank Transfer'],
  Insurance: ['insurance', 'Insurance settlement'],
  'Corporate Account': ['corporate', 'Corporate account'],
}

const slug = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'unknown'
const sum = (values: number[]) => fromN(values.reduce((s, v) => s + toN(v), 0))

/**
 * One-off import of the previous billing records into Billing v2.
 * Legacy collections are only READ — they remain intact as the backup.
 * Ids are deterministic and existing v2 records are skipped, so re-running is harmless.
 * Original document numbers are kept and reserved so they can never be reissued.
 */
export async function migrateLegacyBilling(
  user: User,
  config: BillingConfig,
  legacy: LegacyBilling,
  existing: { documents: Set<string>; payments: Set<string>; creditNotes: Set<string>; accounts: Set<string>; items: Set<string> },
): Promise<MigrationReport> {
  const now = new Date().toISOString()
  const ops: { path: [string, string]; data: object }[] = []
  const put = (col: string, id: string, data: object) => ops.push({ path: [col, id], data: clean(data) })
  const numbersBySeries: Record<NumberSeries, string[]> = { invoice: [], quotation: [], order: [], receipt: [], creditNote: [], refund: [] }
  const reserve = (series: NumberSeries, number: string, ownerId: string) => {
    if (!number) return
    numbersBySeries[series].push(number)
    put(BCOL.numbers, numberRef(series, number).id, { ownerId, series, number, at: now, migrated: true })
  }
  const counts: MigrationReport['counts'] = {}
  const count = (key: string, legacyN: number, migrated: number, skipped: number) => (counts[key] = { legacy: legacyN, migrated, skipped })
  const skipped: string[] = []
  const mismatches: string[] = []

  // ── Accounts ──
  const patients = new Map(legacy.patients.map((p) => [p.id, p]))
  const walkIns = new Map(legacy.walkInClients.map((w) => [w.id, w]))
  const accounts = new Map<string, BillingAccount>()
  const accountFor = (customerType: 'Patient' | 'WalkIn', customerId: string, fallbackName?: string): BillingAccount => {
    if (customerType === 'Patient' && patients.has(customerId)) {
      const p = patients.get(customerId)!
      const id = `acct-pat-${p.id}`
      if (!accounts.has(id)) accounts.set(id, { id, type: 'Patient', name: `${p.firstName} ${p.lastName}`.trim(), patientId: p.id, bmcFileNumber: p.bmcFileNumber, phone: p.contact, address: p.address, status: 'Active', createdAt: now, createdByName: user.fullName })
      return accounts.get(id)!
    }
    const w = walkIns.get(customerId)
    const name = w?.name ?? fallbackName ?? 'Walk-in customer'
    const id = w ? `acct-walk-${w.id}` : `acct-walk-${slug(name)}`
    if (!accounts.has(id)) accounts.set(id, { id, type: 'WalkIn', name, phone: w?.phone, email: w?.email, address: w?.address, status: 'Active', legacyRef: w?.id, createdAt: now, createdByName: user.fullName })
    return accounts.get(id)!
  }
  legacy.walkInClients.forEach((w) => accountFor('WalkIn', w.id))

  const issuer = { business: config.business, appearance: config.appearance, regional: config.regional }
  const legacyLine = (item: LegacyInvoice['items'][number]): BillLine => {
    const grossN = Math.round(item.quantity * toN(item.unitPrice))
    const discountN = Math.round((grossN * (Number(item.discount) || 0)) / 100)
    return {
      id: item.id, itemId: item.itemId, description: item.serviceName + (item.description ? ` — ${item.description}` : ''),
      quantity: item.quantity, unitPrice: item.unitPrice, discountPct: Number(item.discount) || 0,
      taxId: item.taxProfileId ?? 'exempt', taxName: item.taxProfileName ?? 'As originally charged', taxRate: Number(item.tax) || 0, taxInclusive: false,
      fixed: true, gross: fromN(grossN), discount: fromN(discountN), tax: 0, total: item.total ?? fromN(grossN - discountN),
    }
  }

  // ── Payments & credits per legacy invoice ──
  const payByInvoice = new Map<string, LegacyPayment[]>()
  legacy.payments.forEach((p) => payByInvoice.set(p.invoiceId, [...(payByInvoice.get(p.invoiceId) ?? []), p]))
  const creditsByInvoice = new Map<string, LegacyCreditNote[]>()
  legacy.creditNotes.filter((c) => c.status !== 'Draft').forEach((c) => creditsByInvoice.set(c.invoiceId, [...(creditsByInvoice.get(c.invoiceId) ?? []), c]))

  // ── Invoices ──
  let invMigrated = 0
  let invSkipped = 0
  const invoiceIds = new Map<string, string>()
  for (const inv of legacy.invoices) {
    const id = `legacy-inv-${inv.id}`
    invoiceIds.set(inv.id, id)
    if (existing.documents.has(id)) { invSkipped += 1; continue }
    const account = accountFor(inv.customerType, inv.customerId, inv.customerName)
    const paid = sum((payByInvoice.get(inv.id) ?? []).map((p) => p.amount))
    const credited = sum((creditsByInvoice.get(inv.id) ?? []).map((c) => c.amount))
    const voided = inv.status === 'Cancelled'
    const balance = voided ? 0 : Math.max(0, subK(subK(inv.totalAmount, paid), credited))
    if (!voided && inv.status !== 'Draft' && toN(balance) !== toN(inv.balanceDue)) {
      mismatches.push(`${inv.invoiceNumber}: old balance ${inv.balanceDue.toFixed(2)}, recalculated from payments/credits ${balance.toFixed(2)}`)
    }
    const record: SalesDocument = {
      id, type: 'invoice', number: inv.status === 'Draft' ? null : inv.invoiceNumber, state: inv.status === 'Draft' ? 'Draft' : voided ? 'Void' : 'Issued',
      accountId: account.id, accountName: account.name, accountType: account.type,
      patientId: account.patientId, patientName: account.type === 'Patient' ? account.name : undefined, bmcFileNumber: account.bmcFileNumber,
      date: inv.date.slice(0, 10), dueDate: (inv.dueDate || inv.date).slice(0, 10), paymentTerms: inv.paymentTerms, notes: inv.notes,
      staffId: inv.salesperson, staffName: inv.createdBy || 'Previous billing system',
      lines: inv.items.map(legacyLine), subtotal: inv.subtotal, discountTotal: inv.totalDiscount, taxTotal: inv.totalTax, total: inv.totalAmount,
      amountPaid: paid, amountCredited: credited, balance,
      issuer, issuedAt: inv.status === 'Draft' ? undefined : inv.createdAt, issuedByName: 'Previous billing system',
      voidedAt: voided ? now : undefined, voidReason: voided ? 'Cancelled in the previous billing system' : undefined,
      legacy: { source: 'invoice', id: inv.id, number: inv.invoiceNumber },
      createdAt: inv.createdAt, createdByName: inv.createdBy || 'Previous billing system', updatedAt: now,
    }
    put(BCOL.documents, id, record)
    if (record.number) reserve('invoice', record.number, id)
    invMigrated += 1
  }
  count('Invoices', legacy.invoices.length, invMigrated, invSkipped)

  // ── Sales receipts → paid invoice + receipt ──
  let srMigrated = 0
  let srSkipped = 0
  for (const sr of legacy.salesReceipts) {
    const id = `legacy-sr-${sr.id}`
    const payId = `legacy-srpay-${sr.id}`
    if (existing.documents.has(id)) { srSkipped += 1; continue }
    const account = accountFor('WalkIn', `sr-${sr.id}`, sr.billTo)
    const lines: BillLine[] = sr.items.map((it) => ({
      id: it.id, itemId: it.itemId, description: it.itemDescription, quantity: it.quantity, unitPrice: it.rate, discountPct: 0,
      taxId: 'exempt', taxName: 'As originally charged', taxRate: 0, taxInclusive: false, fixed: true, gross: it.amount, discount: 0, tax: 0, total: it.amount,
    }))
    put(BCOL.documents, id, {
      id, type: 'invoice', number: sr.salesReceiptNumber, state: 'Issued', accountId: account.id, accountName: account.name, accountType: account.type,
      date: sr.visitDate.slice(0, 10), dueDate: sr.visitDate.slice(0, 10), staffId: sr.salespersonId, staffName: sr.salespersonName,
      lines, subtotal: sr.totalAmount, discountTotal: 0, taxTotal: 0, total: sr.totalAmount, amountPaid: sr.totalAmount, amountCredited: 0, balance: 0,
      issuer, issuedAt: sr.createdAt, issuedByName: sr.salespersonName, legacy: { source: 'sales-receipt', id: sr.id, number: sr.salesReceiptNumber },
      createdAt: sr.createdAt, createdByName: sr.createdBy, updatedAt: now,
    } as SalesDocument)
    reserve('invoice', sr.salesReceiptNumber, id)
    if (!existing.payments.has(payId)) {
      put(BCOL.payments, payId, {
        id: payId, receiptNumber: sr.salesReceiptNumber, kind: 'Payment', accountId: account.id, accountName: account.name, date: sr.visitDate.slice(0, 10),
        amount: sr.totalAmount, amountTendered: sr.totalAmount, changeGiven: 0, methodId: 'cash', methodName: 'Cash (sales receipt)',
        allocations: [{ invoiceId: id, invoiceNumber: sr.salesReceiptNumber, amount: sr.totalAmount, at: sr.createdAt, byName: sr.salespersonName }],
        unallocated: 0, refunded: 0, state: 'Posted', receivedById: sr.salespersonId, receivedByName: sr.salespersonName,
        legacy: { id: sr.id, number: sr.salesReceiptNumber }, createdAt: sr.createdAt,
      } as BillPayment)
      reserve('receipt', sr.salesReceiptNumber, payId)
    }
    srMigrated += 1
  }
  count('Sales receipts', legacy.salesReceipts.length, srMigrated, srSkipped)

  // ── Payments ──
  let payMigrated = 0
  let paySkipped = 0
  for (const p of legacy.payments) {
    const id = `legacy-pay-${p.id}`
    if (existing.payments.has(id)) { paySkipped += 1; continue }
    const invoiceId = invoiceIds.get(p.invoiceId)
    const legacyInv = legacy.invoices.find((i) => i.id === p.invoiceId)
    const account = accountFor(p.customerType, p.customerId, legacyInv?.customerName)
    const [methodId, methodName] = methodMap[p.method] ?? ['cash', p.method]
    if (!invoiceId) skipped.push(`Receipt ${p.receiptNumber}: its invoice no longer exists — imported as account credit.`)
    put(BCOL.payments, id, {
      id, receiptNumber: p.receiptNumber, kind: 'Payment', accountId: account.id, accountName: account.name, date: p.date.slice(0, 10),
      amount: p.amount, amountTendered: p.amount, changeGiven: 0, methodId, methodName, notes: p.notes,
      allocations: invoiceId ? [{ invoiceId, invoiceNumber: legacyInv?.invoiceNumber ?? '', amount: p.amount, at: p.createdAt, byName: 'Previous billing system' }] : [],
      unallocated: invoiceId ? 0 : p.amount, refunded: 0, state: 'Posted', receivedById: p.cashier, receivedByName: 'Previous billing system',
      legacy: { id: p.id, number: p.receiptNumber }, createdAt: p.createdAt,
    } as BillPayment)
    reserve('receipt', p.receiptNumber, id)
    payMigrated += 1
  }
  count('Payments', legacy.payments.length, payMigrated, paySkipped)

  // ── Credit notes ──
  let cnMigrated = 0
  let cnSkipped = 0
  for (const c of legacy.creditNotes) {
    const id = `legacy-cn-${c.id}`
    if (c.status === 'Draft') { cnSkipped += 1; skipped.push(`Credit note ${c.creditNoteNumber}: still a draft in the old system — not imported.`); continue }
    if (existing.creditNotes.has(id)) { cnSkipped += 1; continue }
    const invoiceId = invoiceIds.get(c.invoiceId)
    const legacyInv = legacy.invoices.find((i) => i.id === c.invoiceId)
    const account = accountFor(c.customerType, c.customerId, legacyInv?.customerName)
    const refunded = c.status === 'Refunded' ? c.amount : 0
    put(BCOL.creditNotes, id, {
      id, number: c.creditNoteNumber, accountId: account.id, accountName: account.name, invoiceId, invoiceNumber: legacyInv?.invoiceNumber,
      date: c.date.slice(0, 10), reason: c.reason, amount: c.amount,
      allocations: invoiceId && !refunded ? [{ invoiceId, invoiceNumber: legacyInv?.invoiceNumber ?? '', amount: c.amount, at: c.createdAt, byName: 'Previous billing system' }] : [],
      unallocated: 0, refunded, state: 'Issued', issuer, createdById: c.createdBy, createdByName: 'Previous billing system',
      legacy: { id: c.id, number: c.creditNoteNumber }, createdAt: c.createdAt,
    } as CreditNote)
    reserve('creditNote', c.creditNoteNumber, id)
    cnMigrated += 1
  }
  count('Credit notes', legacy.creditNotes.length, cnMigrated, cnSkipped)

  // ── Quotations ──
  let qMigrated = 0
  let qSkipped = 0
  for (const q of legacy.quotations) {
    const id = `legacy-quo-${q.id}`
    if (existing.documents.has(id)) { qSkipped += 1; continue }
    const account = accountFor(q.customerType, q.customerId)
    put(BCOL.documents, id, {
      id, type: 'quotation', number: q.quotationNumber, state: q.status === 'Invoiced' ? 'Converted' : q.status === 'Expired' ? 'Declined' : q.status,
      accountId: account.id, accountName: account.name, accountType: account.type, patientId: account.patientId, bmcFileNumber: account.bmcFileNumber,
      date: q.date.slice(0, 10), validUntil: q.validUntil?.slice(0, 10), paymentTerms: q.terms, notes: q.notes, staffId: q.salesperson, staffName: q.createdBy,
      lines: q.items.map(legacyLine), subtotal: q.subtotal, discountTotal: q.totalDiscount, taxTotal: q.totalTax, total: q.totalAmount,
      amountPaid: 0, amountCredited: 0, balance: q.totalAmount, legacy: { source: 'quotation', id: q.id, number: q.quotationNumber },
      createdAt: q.createdAt, createdByName: q.createdBy, updatedAt: now,
    } as SalesDocument)
    reserve('quotation', q.quotationNumber, id)
    qMigrated += 1
  }
  count('Quotations', legacy.quotations.length, qMigrated, qSkipped)

  // ── Catalogue ──
  let itemMigrated = 0
  for (const it of legacy.billingItems) {
    const id = `item-${it.id}`
    if (existing.items.has(id)) continue
    const cat = it.category.toLowerCase()
    const sourceType: CatalogueItem['sourceType'] = cat.includes('lab') ? 'lab' : cat.includes('radio') || cat.includes('imaging') ? 'radiology' : cat.includes('consult') ? 'consultation' : cat.includes('pharm') || cat.includes('drug') ? 'pharmacy' : cat.includes('procedure') ? 'procedure' : 'other'
    const tax = config.taxes.rates.find((t) => t.rate === it.taxRate) ?? config.taxes.rates.find((t) => t.id === 'exempt')!
    put(BCOL.items, id, {
      id, code: it.itemCode, name: it.serviceName, category: it.category, department: it.department, sourceType, description: it.description,
      unitPrice: it.unitPrice, taxId: tax.id, discountAllowed: it.discountEligibility, matchNames: [], status: it.status === 'Active' ? 'Active' : 'Inactive',
      priceHistory: [{ price: it.unitPrice, from: it.createdAt, byName: 'Previous billing system' }], legacyRef: it.id, createdAt: it.createdAt, updatedAt: now,
    } as CatalogueItem)
    itemMigrated += 1
  }
  count('Items & services', legacy.billingItems.length, itemMigrated, legacy.billingItems.length - itemMigrated)

  let acctMigrated = 0
  accounts.forEach((acct) => {
    if (existing.accounts.has(acct.id)) return
    put(BCOL.accounts, acct.id, acct)
    acctMigrated += 1
  })
  count('Accounts', accounts.size, acctMigrated, accounts.size - acctMigrated)

  // ── Counters: continue numbering after the highest imported number for each prefix ──
  for (const series of Object.keys(numbersBySeries) as NumberSeries[]) {
    const { prefix } = config.numbering[series]
    const max = numbersBySeries[series].reduce((m, n) => {
      if (!n.startsWith(prefix)) return m
      const value = Number(n.slice(prefix.length).replace(/\D/g, ''))
      return Number.isFinite(value) ? Math.max(m, value) : m
    }, 0)
    if (max > 0) {
      const current = Number((await getDoc(counterRef(series))).data()?.last) || 0
      if (max > current) put(BCOL.counters, counterRef(series).id, { last: max, updatedAt: now })
    }
  }

  // ── Reconciliation ──
  const issuedLegacy = legacy.invoices.filter((i) => i.status !== 'Draft' && i.status !== 'Cancelled')
  const migratedReceivable = issuedLegacy.map((inv) => {
    const paid = sum((payByInvoice.get(inv.id) ?? []).map((p) => p.amount))
    const credited = sum((creditsByInvoice.get(inv.id) ?? []).map((c) => c.amount))
    return Math.max(0, subK(subK(inv.totalAmount, paid), credited))
  })
  const totals: MigrationReport['totals'] = [
    { label: 'Invoiced (issued, not cancelled)', legacy: sum(issuedLegacy.map((i) => i.totalAmount)), migrated: sum(issuedLegacy.map((i) => i.totalAmount)) },
    { label: 'Payments received', legacy: sum(legacy.payments.map((p) => p.amount)), migrated: sum(legacy.payments.map((p) => p.amount)) },
    { label: 'Credit notes (issued)', legacy: sum(legacy.creditNotes.filter((c) => c.status !== 'Draft').map((c) => c.amount)), migrated: sum(legacy.creditNotes.filter((c) => c.status !== 'Draft').map((c) => c.amount)) },
    { label: 'Sales receipts', legacy: sum(legacy.salesReceipts.map((s) => s.totalAmount)), migrated: sum(legacy.salesReceipts.map((s) => s.totalAmount)) },
    { label: 'Outstanding balances', legacy: sum(issuedLegacy.map((i) => i.balanceDue)), migrated: addK(...migratedReceivable, 0) },
  ]

  const report: MigrationReport = { ranAt: now, ranByName: user.fullName, counts, totals, mismatches, skipped }
  put(BCOL.config, CONFIG_ID, { ...config, migration: { status: 'completed', report }, updatedAt: now, updatedByName: user.fullName })
  put(BCOL.audit, `bau-migration-${Date.now()}`, {
    id: `bau-migration-${Date.now()}`, at: now, userId: user.id, userName: user.fullName, action: 'Legacy billing migrated', entity: 'Migration', entityId: 'migration',
    summary: Object.entries(counts).map(([k, v]) => `${k}: ${v.migrated}/${v.legacy}`).join(' · '),
  })

  // Commit in chunks (Firestore batch limit is 500 writes). The config write is last,
  // so the migration is only marked complete once every record has been saved.
  for (let i = 0; i < ops.length; i += 400) {
    const batch = writeBatch(db)
    ops.slice(i, i + 400).forEach((op) => batch.set(doc(db, op.path[0], op.path[1]), op.data))
    await batch.commit()
  }
  return report
}
